import { ArticleDTO, ArticleModel } from "@/entities/Article";
import { computed, ref } from "vue";
import useAPIRequest from "@/composables/useAPIRequest";

interface FetchArticlesQueryParams {
  offsetStart: number;
  offsetEnd: number;
}

const fetchArticlesQuery = `
*[
  _type == 'article'
  && !(_id in path("drafts.**"))
  && date <= now()
  && defined(slug)
  && defined(image)
  && defined(title)
  && defined(body)
] | order(date desc) [$offsetStart...$offsetEnd]
{
  _id,
  title,
  "slug": slug.current,
  date,
  "image": image.asset->url
}`;

export type ArticleWithMetadata = {
  page: number;
  articles: ArticleModel[];
  pending: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
};

// Storing as functions for global access
const useArticlesWithMetadata = () =>
  ref<Record<string, ArticleWithMetadata>>({});

const ITEMS_PER_PAGE = 20;

export default function useArticles() {
  const articlesWithMetadata = useArticlesWithMetadata();

  const articles = computed(() => {
    return Object.values(articlesWithMetadata.value)
      .map((aWM) => aWM.articles)
      .flat();
  });

  const fetchArticles = async (page = 1) => {
    articlesWithMetadata.value = {
      ...articlesWithMetadata.value,
      [page]: {
        articles: articlesWithMetadata.value[page]?.articles || [],
        page: 1,
        pending: true,
        error: null,
      },
    };

    const params: FetchArticlesQueryParams = {
      offsetStart: (page - 1) * ITEMS_PER_PAGE,
      offsetEnd: page * ITEMS_PER_PAGE,
    };

    try {
      const { fetch, requestState } = useAPIRequest<ArticleDTO[]>();
      await fetch(fetchArticlesQuery, params);
      const data = requestState.value.data;
      articlesWithMetadata.value = {
        ...articlesWithMetadata.value,
        [page]: {
          articles: data ? data.map((n) => new ArticleModel(n)) : [],
          page,
          pending: false,
          error: null,
        },
      };
    } catch (error) {
      console.error("error in request", error);
      articlesWithMetadata.value = {
        ...articlesWithMetadata.value,
        [page]: {
          articles: [],
          page,
          pending: false,
          error,
        },
      };
    }
  };

  return {
    fetchArticles,
    articles,
    articlesWithMetadata,
  };
}
